import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const alarmsQuery = `
 query MyQuery {
  getInAlarms {
    alarmName
    stateReason
    metricName
    namespace
    statistic
    threshold
    comparisonOperator
    evaluationPeriods
    datapointsToAlarm
    dimensions {
      name
      value
    }
  }
}
`;

const AlertsPage = ({ open, onClose, api }) => {
  const [alertsData, setAlertsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const fetchAlarmsData = useCallback(async () => {
    if (!api) {
      setError('API not available');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.post('', { query: alarmsQuery });
      
      if (response.data?.data?.getInAlarms) {
        const alarms = response.data.data.getInAlarms;
        console.log('Fetched alarms:', alarms);
        
        if (alarms.length === 0) {
          setError('No active alarms currently');
        } else {
          // Add index for unique identification
          const alarmsWithId = alarms.map((alarm, index) => ({
            ...alarm,
            id: index + 1
          }));
          setAlertsData(alarmsWithId);
        }
      } else if (response.data?.errors) {
        throw new Error(response.data.errors[0]?.message || "Unknown API error");
      } else {
        setError('No active alarms currently');
      }
    } catch (fetchError) {
      console.error('Error fetching alarms data:', fetchError);
      setError(`Failed to load alarms: ${fetchError.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (open && api) {
      fetchAlarmsData();
      setCurrentPage(1);
    }
  }, [open, fetchAlarmsData]);

  const handleRefresh = () => {
    fetchAlarmsData();
    setCurrentPage(1);
  };

  const formatDimensions = (dimensions) => {
    if (!dimensions || dimensions.length === 0) return 'N/A';
    return dimensions.map(dim => `${dim.name}: ${dim.value}`).join(', ');
  };
  const totalPages = Math.ceil(alertsData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = alertsData.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const maxPagesToShow = 7; // Show more pages when possible
    let pageNumbers = [];
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is small
      pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      // Always show first page
      pageNumbers.push(1);
      
      if (currentPage <= 4) {
        // Near the beginning
        for (let i = 2; i <= Math.min(5, totalPages - 1); i++) {
          pageNumbers.push(i);
        }
        if (totalPages > 5) {
          pageNumbers.push('...');
          pageNumbers.push(totalPages);
        }
      } else if (currentPage >= totalPages - 3) {
        // Near the end
        pageNumbers.push('...');
        for (let i = Math.max(totalPages - 4, 2); i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // In the middle
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-[98%] max-w-[1400px] h-[95vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center bg-[#003366]/5 shrink-0">
          <h2 className="text-2xl font-bold text-[#003366]">Active Alarms</h2>
          <div className="flex gap-2 items-center">
           
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-600">Loading alarms...</p>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-gray-600 mb-4">{error}</p>
                  <button
                    onClick={handleRefresh}
                    className="px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#003366]/90 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-[#003366]/5">
                    <tr>
                      <th className="px-4 py-4 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Alarm Name
                      </th>
                      <th className="px-4 py-4 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Metric Name
                      </th>
                      <th className="px-4 py-4 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Namespace
                      </th>
                      <th className="px-4 py-4 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Statistic
                      </th>
                      <th className="px-4 py-4 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Threshold
                      </th>
                      <th className="px-4 py-4 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Comparison
                      </th>
                      <th className="px-4 py-4 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Evaluation Periods
                      </th>
                      <th className="px-4 py-4 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        State Reason
                      </th>
                      <th className="px-4 py-4 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                        Dimensions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentData.map((alarm) => (
                      <tr key={alarm.id} className="hover:bg-[#003366]/5">
                        <td className="px-4 py-4 text-[#003366] font-medium max-w-xs">
                          <div className="truncate" title={alarm.alarmName}>
                            {alarm.alarmName || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          <div className="truncate" title={alarm.metricName}>
                            {alarm.metricName || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          <div className="truncate" title={alarm.namespace}>
                            {alarm.namespace || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {alarm.statistic || 'N/A'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {alarm.threshold || 'N/A'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {alarm.comparisonOperator || 'N/A'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700 text-center">
                          <div>
                            {alarm.evaluationPeriods || 'N/A'}
                            {alarm.datapointsToAlarm && (
                              <div className="text-xs text-gray-500">
                                ({alarm.datapointsToAlarm} datapoints)
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 max-w-sm text-gray-700">
                          <div className="truncate text-sm" title={alarm.stateReason}>
                            {alarm.stateReason || 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 py-4 max-w-xs text-sm text-gray-700">
                          <div className="truncate" title={formatDimensions(alarm.dimensions)}>
                            {formatDimensions(alarm.dimensions)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {!isLoading && !error && alertsData.length > 0 && (
          <div className="border-t border-gray-200 px-6 py-4 bg-white shrink-0">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-700">
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, alertsData.length)} of {alertsData.length}{" "}
                  entries
                </span>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Items per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      const newItemsPerPage = parseInt(e.target.value);
                      setItemsPerPage(newItemsPerPage);
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-sm rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600"
                  title="First page"
                >
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-600" />
                </button>
                
                <div className="flex items-center gap-1">
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-gray-600 text-sm">...</span>
                    ) : (
                      <button
                        key={`page-${page}`}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                          currentPage === page
                            ? "bg-[#003366] text-white font-medium"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>
                
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="px-2 py-1 text-sm rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600"
                  title="Last page"
                >
                  »»
                </button>
              </div>
            </div>
            
            {totalPages > 10 && (
              <div className="mt-3 flex justify-center">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Go to page:</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={currentPage}
                    onChange={(e) => {
                      const page = parseInt(e.target.value);
                      if (page >= 1 && page <= totalPages) {
                        setCurrentPage(page);
                      }
                    }}
                    className="w-16 px-2 py-1 text-sm border border-gray-300 rounded text-center"
                  />
                  <span className="text-sm text-gray-600">of {totalPages}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsPage;